import tippy, { type Props } from "tippy.js";

export const DEFAULT_TOOLTIP_CLASSES =
  "rounded-md border border-white/10 bg-gray-600 py-1 px-2 text-xs font-medium text-white dark:bg-gray-700 animate-fade-in-short";

export interface TippyProps extends Partial<Props> {
  template?: string;
  element?: HTMLElement;
}

/** Create a tooltip for an element.
 *
 * If the content is reactive, pass it in as `content` prop, otherwise the action will use the
 * `title` attribute or `aria-label` attribute if set.
 * */
export function tooltip(node: HTMLElement, props?: TippyProps) {
  // Determine the title to show. We want to prefer the custom content passed in first, then the
  // HTML title attribute then the aria-label in that order.

  const label = node.getAttribute("aria-label");
  const content = props?.element || props?.content || node.title || label || undefined;
  // Let's make sure customNode "aria-label" attribute is set so our element is accessible:
  // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-label_attribute
  if (!label && content && typeof content === "string") {
    node.setAttribute("aria-label", content);
  }

  // prevent default browser behaviour if title set
  node.title = "";
  // template + element not a valid tippy prop
  delete props?.template;
  const tippyProps = props;
  delete tippyProps?.element;
  // Support any of the Tippy props by forwarding all "params":
  // https://atomiks.github.io/tippyjs/v6/all-props/
  let tip = tippy(node as Element, {
    content,
    ...tippyProps,
    appendTo: document.body,
    delay: props?.trigger == "click" ? 0 : [350, 0],
    touch: false,
    // Set duration to 0 for immediate disappearance
    duration: [300, 0],
    // Disable animation
    animation: false,
  });

  // style non-custom tooltips
  if (typeof content === "string") {
    tip.popper.className = DEFAULT_TOOLTIP_CLASSES;
  }

  // close tippy when a click is registered in interactive mode
  if (props?.interactive) props?.element?.addEventListener("mouseup", tip.hide);

  // create observer to watch for title changes
  const observer = new MutationObserver(() => {
    const label = node.getAttribute("aria-label");
    const content =
      props?.element || props?.content || node.getAttribute("title") || label || undefined;
    tip.setContent(content || "");
    // disconnect observer to prevent infinite loop
    observer.disconnect();
    // reset title to prevent default browser behaviour
    node.title = "";
    // re-observe title changes
    observer.observe(node, { attributes: true, attributeFilter: ["title"] });
  });

  observer.observe(node, { attributes: true, attributeFilter: ["title"] });

  return {
    // If the props change, let's update the Tippy instance:
    update: (props: TippyProps) => {
      props?.element?.removeEventListener("mouseup", tip.hide);
      props?.element?.addEventListener("mouseup", tip.hide);
      const label = node.getAttribute("aria-label");

      const content = props?.element || props?.content || node.title || label || undefined;
      // Let's make sure customNode "aria-label" attribute is set so our element is accessible:
      // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/ARIA_Techniques/Using_the_aria-label_attribute
      if (!label && content && typeof content === "string") {
        node.setAttribute("aria-label", content);
      }
      // dom element has potentially rerendered, so ensure new tip has been stashed away by tippy
      tip.destroy();
      tip = tippy(node as Element, {
        content,
        ...props,
        appendTo: document.body,
        delay: props?.trigger == "click" ? 0 : [350, 0],
        touch: false,
        // Set duration to 0 for immediate disappearance
        duration: [300, 0],
        // Disable animation
        animation: false,
      });
      // style non-custom tooltips
      if (typeof content === "string") {
        tip.popper.className = DEFAULT_TOOLTIP_CLASSES;
      }
    },

    // Clean up the Tippy instance on unmount:
    destroy: () => {
      props?.element?.removeEventListener("mouseup", tip.hide);
      observer.disconnect();
      tip.destroy();
    },
  };
}
